import { Can } from "@/casl-provider";
import { useCustomerBulkDeleteMutation } from "@/common/api/customer/use-customer-bulk-delete.mutation";
import { useCustomerFullSyncMutation } from "@/common/api/customer/use-customer-full-sync.mutation";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { CustomerTable } from "@/components/data-table/predefined/customer-table/customer-table";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CustomerCompanyActionButtons } from "./_components/customer-company-action-buttons";
import { CustomerCompanySelectionDialog } from "./_components/customer-company-selection-dialog";
import {
  createEmptyCustomerCompanySelection,
  hasCustomerCompanySelection,
  toggleCustomerCompany,
  toggleUnassignedCustomers,
  type CustomerCompanyAction,
  type CustomerCompanySelection,
} from "./_helpers/customer-company-selection";

export const CustomerView = () => {
  const { t } = useTranslation(["masterdata", "common"]);
  const { mutateAsync: triggerFullSync, isPending: isFullSyncPending } = useCustomerFullSyncMutation();
  const { mutateAsync: deleteCustomers, isPending: isBulkDeletePending } = useCustomerBulkDeleteMutation();
  const [companyAction, setCompanyAction] = useState<CustomerCompanyAction | null>(null);
  const [companySelection, setCompanySelection] = useState<CustomerCompanySelection>(
    createEmptyCustomerCompanySelection,
  );

  const handleFullSync = useCallback(
    async (force: boolean, selection: CustomerCompanySelection): Promise<void> => {
      await toastMutationStatus({
        mutationFn: () => triggerFullSync({ force, ...selection }),
        errorMessage: t("masterdata:CustomerFullSyncError"),
        onSuccess: () => {
          toast.success(
            t("masterdata:CustomerFullSyncStarted", {
              mode: t(force ? "masterdata:CustomerFullSyncForced" : "masterdata:CustomerFullSyncStandard"),
            }),
          );
        },
      });
    },
    [triggerFullSync, t],
  );

  const handleBulkDelete = useCallback(
    async (selection: CustomerCompanySelection): Promise<void> => {
      await toastMutationStatus({
        mutationFn: () => deleteCustomers(selection),
        errorMessage: t("masterdata:CustomerBulkDeleteError"),
        onSuccess: (bulkDeleteResponse) => {
          toast.success(
            t("masterdata:CustomerBulkDeleteSucceeded", {
              deletedCustomerCount: bulkDeleteResponse.deletedCustomerCount,
            }),
          );
        },
      });
    },
    [deleteCustomers, t],
  );

  const closeCompanyActionDialog = useCallback(() => {
    setCompanyAction(null);
    setCompanySelection(createEmptyCustomerCompanySelection());
  }, []);

  const handleConfirmCompanyAction = useCallback((): void => {
    if (!companyAction || !hasCustomerCompanySelection(companySelection)) {
      return;
    }

    const action = companyAction;
    const selection = companySelection;
    closeCompanyActionDialog();

    if (action.type === "bulk-delete") {
      void handleBulkDelete(selection);
      return;
    }

    void handleFullSync(action.force, selection);
  }, [closeCompanyActionDialog, companyAction, companySelection, handleBulkDelete, handleFullSync]);

  const handleToggleCompany = useCallback((companyUuid: string) => {
    setCompanySelection((selection) => toggleCustomerCompany(selection, companyUuid));
  }, []);

  const handleToggleUnassignedCustomers = useCallback(() => {
    setCompanySelection(toggleUnassignedCustomers);
  }, []);

  const isCompanyActionPending = isFullSyncPending || isBulkDeletePending;

  return (
    <Can I="read" a="Customer">
      <CenteredLayout
        variant={CenteredLayoutVariant.Table}
        title={t("masterdata:Customers")}
        subTitle={t("common:SearchFilterAndManageEntriesDescription")}
      >
        <div className="flex min-h-0 flex-1 flex-col">
          <CustomerTable
            showExportButton
            headerComponent={
              <Can I="manage" a="Customer">
                <Can I="read" a="Company">
                  <CustomerCompanyActionButtons
                    isFullSyncPending={isFullSyncPending}
                    isBulkDeletePending={isBulkDeletePending}
                    onSelectAction={setCompanyAction}
                  />
                </Can>
              </Can>
            }
          />
        </div>
      </CenteredLayout>
      <CustomerCompanySelectionDialog
        action={companyAction}
        selection={companySelection}
        isPending={isCompanyActionPending}
        onClose={closeCompanyActionDialog}
        onConfirm={handleConfirmCompanyAction}
        onToggleCompany={handleToggleCompany}
        onToggleUnassignedCustomers={handleToggleUnassignedCustomers}
      />
    </Can>
  );
};
